import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { setShowGigabot } from '../../store/slices/globalSlice';
import { initializeStarCanvas } from 'utils';
import { Gigabot, ErrorBoundary } from 'components';
import './PageLayout.scss';

/**
 * PageLayout Component
 *
 * Reusable page layout component that provides:
 * - Star canvas background
 * - Gigabot integration with resize handling
 * - Error message display
 * - Consistent glassmorphism container styling
 * - Responsive layout with Gigabot panel
 *
 * @component
 * @example
 * ```jsx
 * <PageLayout
 *   page-layout-content="profile-container"
 *   error={error}
 *   onErrorDismiss={() => setError(null)}
 *   gigabotConfig={{
 *     placeholder: 'Ask about profile...',
 *     context: 'profile',
 *     contextData: profileData,
 *     initialMessage: welcomeMessage,
 *     onActionRun: handleActionRun,
 *     onActionSkip: handleActionSkip,
 *     ariaProps: ariaProps
 *   }}
 *   starCanvasConfig={{
 *     starCount: 400,
 *     maxSpeed: 0.7
 *   }}
 * >
 *   <YourPageContent />
 * </PageLayout>
 * ```
 */
// Default star canvas configuration - defined outside component to prevent re-renders
const DEFAULT_STAR_CONFIG = { starCount: 400, maxSpeed: 0.7 };

// Default Gigabot panel width - used when gigabotWidth is not yet set
const GIGABOT_DEFAULT_WIDTH = 480;

export const PageLayout = ({
  children,
  error = null,
  onErrorDismiss = null,
  gigabotConfig = null,
  starCanvasConfig,
  loading = false,
  loadingMessage = 'Loading...',
  loadingSkeleton = null,
  pageTitle = '',
  pageDescription = '',
  pageLayoutContentClassName = '',
}) => {
  const dispatch = useDispatch();
  const { showGigabot } = useSelector((state) => state.global);
  const [gigabotWidth, setGigabotWidth] = useState(0);

  // Memoize star canvas config to prevent unnecessary re-renders
  const starConfig = useMemo(
    () => starCanvasConfig || DEFAULT_STAR_CONFIG,
    [starCanvasConfig?.starCount, starCanvasConfig?.maxSpeed]
  );

  /**
   * Handle Gigabot close
   */
  const handleGigabotClose = () => {
    dispatch(setShowGigabot(false));
    setGigabotWidth(0);
  };

  /**
   * Handle Gigabot open
   */
  const handleGigabotOpen = () => {
    dispatch(setShowGigabot(true));
  };

  /**
   * Handle Gigabot resize
   */
  const handleGigabotResize = (width) => {
    setGigabotWidth(width);
  };

  /**
   * Initialize star canvas background
   */
  useEffect(() => {
    const cleanup = initializeStarCanvas('star-canvas', starConfig);
    return cleanup;
  }, [starConfig]);

  // Set CSS custom property for Gigabot width on document root
  // This allows modals (portaled to body) to access the current Gigabot width
  useEffect(() => {
    const width = showGigabot
      ? gigabotWidth > 0
        ? gigabotWidth
        : GIGABOT_DEFAULT_WIDTH
      : 0;
    document.documentElement.style.setProperty('--gigabot-width', `${width}px`);

    return () => {
      document.documentElement.style.removeProperty('--gigabot-width');
    };
  }, [showGigabot, gigabotWidth]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Command+K for global search focus
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        const searchInput = document.querySelector('.search-input');
        if (searchInput) {
          searchInput.focus();
        }
      }
      // Command+L for Gigabot toggle
      if (e.key === 'l' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        dispatch(setShowGigabot(!showGigabot));
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [dispatch, showGigabot]);

  return (
    <div className='gigacore-theme'>
      <canvas id='star-canvas'></canvas>
      <div
        className={`gigacore-container page-layout-wrapper ${
          showGigabot && gigabotConfig ? 'with-gigabot' : ''
        }`}
        style={{
          paddingRight: gigabotWidth > 0 ? `${gigabotWidth}px` : undefined,
        }}
      >
        {pageTitle && (
          <div className='page-layout-title-section'>
            <div className='title-content'>
              <h1>{pageTitle}</h1>
              <p>{pageDescription}</p>
            </div>
          </div>
        )}
        {/* Loading state */}
        {loading && (
          <div className='page-layout-loading'>
            {loadingSkeleton ? (
              loadingSkeleton
            ) : (
              <>
                <div className='loading'>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <p>{loadingMessage}</p>
              </>
            )}
          </div>
        )}

        {/* Error message */}
        {error && onErrorDismiss && (
          <div className='page-layout-error'>
            <p>{error}</p>
            <button onClick={onErrorDismiss}>Dismiss</button>
          </div>
        )}

        {/* Page content */}
        {!loading && (
          <div className={`page-layout-content ${pageLayoutContentClassName}`}>
            {children}
          </div>
        )}
      </div>

      {/* Gigabot Chat Panel - wrapped in ErrorBoundary */}
      {gigabotConfig && (
        <ErrorBoundary
          title='Chat Assistant Unavailable'
          message='The chat assistant encountered an error. Please try again.'
          showRetry={true}
        >
          <Gigabot
            isOpen={showGigabot}
            onClose={handleGigabotClose}
            onOpen={handleGigabotOpen}
            onResize={handleGigabotResize}
            placeholder={gigabotConfig.placeholder || 'Ask me anything...'}
            context={gigabotConfig.context || 'general'}
            contextData={gigabotConfig.contextData || {}}
            initialMessage={gigabotConfig.initialMessage || null}
            onActionRun={gigabotConfig.onActionRun || null}
            onActionSkip={gigabotConfig.onActionSkip || null}
            ariaProps={gigabotConfig.ariaProps || {}}
            prefilledContent={gigabotConfig.prefilledContent || ''}
            prefilledTrigger={gigabotConfig.prefilledTrigger || 0}
            containerElement={gigabotConfig.containerElement || 'div'}
            onMessage={gigabotConfig.onMessage || (() => {})}
            sessionId={gigabotConfig.sessionId || null}
            resizable={gigabotConfig.resizable ?? true}
          />
        </ErrorBoundary>
      )}
    </div>
  );
};

PageLayout.propTypes = {
  /** Child components to render inside the layout */
  children: PropTypes.node.isRequired,
  /** Error message to display */
  error: PropTypes.string,
  /** Callback when error is dismissed */
  onErrorDismiss: PropTypes.func,
  /** Gigabot configuration object */
  gigabotConfig: PropTypes.shape({
    placeholder: PropTypes.string,
    context: PropTypes.string,
    contextData: PropTypes.object,
    initialMessage: PropTypes.object,
    onActionRun: PropTypes.func,
    onActionSkip: PropTypes.func,
    ariaProps: PropTypes.object,
    prefilledContent: PropTypes.string,
    prefilledTrigger: PropTypes.number,
    containerElement: PropTypes.string,
    onMessage: PropTypes.func,
    sessionId: PropTypes.string,
    resizable: PropTypes.bool,
  }),
  /** Star canvas configuration */
  starCanvasConfig: PropTypes.shape({
    starCount: PropTypes.number,
    maxSpeed: PropTypes.number,
  }),
  /** Loading state */
  loading: PropTypes.bool,
  /** Loading message text */
  loadingMessage: PropTypes.string,
  /** Custom loading skeleton component */
  loadingSkeleton: PropTypes.node,
  /** Page title to display in header */
  pageTitle: PropTypes.string,
  /** Page description to display in header */
  pageDescription: PropTypes.string,
  /** Page layout content class name */
  pageLayoutContentClassName: PropTypes.string,
};
